import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "../portal/api";
import OverviewTab from "../components/admin/engagement/OverviewTab";
import EvidenceReviewTab from "../components/admin/engagement/EvidenceReviewTab";
import OutputsAdminTab from "../components/admin/engagement/OutputsAdminTab";
import MessagesAdminTab from "../components/admin/engagement/MessagesAdminTab";
import AuditTab from "../components/admin/engagement/AuditTab";

const TABS = ["overview", "evidence", "outputs", "messages", "audit"];

export default function AdminEngagement() {
  const { id } = useParams();
  const [authed, setAuthed] = useState(null);
  const [eng, setEng] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    http.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const loadEng = useCallback(() => {
    http.get(`/portal/admin/engagements/${id}`)
      .then(({ data }) => setEng(data))
      .catch(() => toast.error("Could not load engagement."));
  }, [id]);
  useEffect(() => { if (authed) loadEng(); }, [authed, loadEng]);

  if (authed === null) return <div className="min-h-screen flex items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</div>;
  if (authed === false) {
    window.location.href = "/admin";
    return null;
  }

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="admin-engagement-page">
      <header className="border-b border-line bg-surface1">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/bigibila-logo.png" alt="Bigibila" className="h-9 w-9" />
            <span className="font-serif text-lg">Bigibila</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">Admin workspace</span>
          </div>
          <Link to="/admin/engagements" className="font-mono text-xs uppercase tracking-[0.15em] text-stone2 hover:text-ember transition-colors duration-300">← All engagements</Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        {!eng ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading engagement…</p>
        ) : (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-2">{eng.organisation_name}</p>
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-8">
              <h1 className="font-serif text-3xl md:text-4xl">{eng.title}</h1>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border border-ember/50 text-ember" data-testid="admin-eng-status">{eng.status}</span>
            </div>
            <div className="flex items-center gap-6 border-b border-line mb-8 flex-wrap">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`admin-eng-tab-${t}`}
                  className={`font-mono text-xs uppercase tracking-[0.2em] pb-3 -mb-px border-b-2 transition-colors duration-300 ${tab === t ? "border-ember text-ember" : "border-transparent text-stone2 hover:text-bone"}`}>
                  {t}
                </button>
              ))}
            </div>
            {tab === "overview" && <OverviewTab eng={eng} reload={loadEng} />}
            {tab === "evidence" && <EvidenceReviewTab engagementId={id} />}
            {tab === "outputs" && <OutputsAdminTab engagementId={id} clients={eng.clients || []} agreementActive={eng.agreement_active} />}
            {tab === "messages" && <MessagesAdminTab engagementId={id} />}
            {tab === "audit" && <AuditTab engagementId={id} />}
          </>
        )}
      </main>
    </div>
  );
}
